import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createHubQueue, type HubQueue } from './data/hubQueue/queue'
import { createMemoryQueueStore } from './data/hubQueue/store'
import type { SliceApi } from './data/types'
import type { Platform } from './platform/platform'

interface RedesignEnv {
  api: SliceApi
  platform: Platform
  /** Часы приложения: стенд и тесты подставляют свои. */
  now: () => Date
  toast: (message: string) => void
  /** Очередь вопросов Штаба на устройстве (чат PWA). */
  hubQueue: HubQueue
  /**
   * Данные экранов сохраняются на устройстве (PWA со снимком, этап 4):
   * имеет смысл подгружать заранее то, что понадобится без сети. В
   * мини-приложениях, на стенде и в тестах по умолчанию - нет.
   */
  savesOffline: boolean
}

const EnvContext = createContext<RedesignEnv | null>(null)

export function useEnv(): RedesignEnv {
  const env = useContext(EnvContext)
  if (!env) throw new Error('useEnv: нет RedesignProvider')
  return env
}

export function RedesignProvider({
  api,
  platform,
  now = () => new Date(),
  hubQueue,
  savesOffline = false,
  children,
}: {
  api: SliceApi
  platform: Platform
  now?: () => Date
  /**
   * Боевой вход даёт очередь в IndexedDB с владельцем-пользователем. Без неё
   * (стенд, тесты) - очередь в памяти одного «пользователя».
   */
  hubQueue?: HubQueue
  /** Кэш экранов сохраняется на устройстве (LiveRedesign с offlineStore). */
  savesOffline?: boolean
  children: ReactNode
}) {
  const [message, setMessage] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()

  const toast = useCallback((text: string) => {
    setMessage(text)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setMessage(null), 3000)
  }, [])

  useEffect(() => () => clearTimeout(timer.current), [])

  const ownQueue = useMemo(
    () =>
      hubQueue
        ? null
        : createHubQueue({ store: createMemoryQueueStore(), api, owner: () => 'local', assistant: () => 'local' }),
    [api, hubQueue],
  )
  const queue = hubQueue ?? ownQueue!
  useEffect(() => queue.attach(), [queue])

  return (
    <EnvContext.Provider value={{ api, platform, now, toast, hubQueue: queue, savesOffline }}>
      {children}
      {message && (
        <div className="pc-toast" role="status">
          {message}
        </div>
      )}
    </EnvContext.Provider>
  )
}
